import {useState} from "react";
import _ from "lodash";
import {Box, IconButton} from "@mui/material";
import {MRT_ColumnDef, MaterialReactTable, useMaterialReactTable} from "material-react-table";
import {CompanyMemberItem} from "../../types/companyMemberItem";
import {CompanyMemberInviteDialog} from "../dialogs/CompanyMemberInviteDialog";
import {CompanyMemberRole} from "../../types/domain/companyMember";
import {CompanyMemberEditDialog} from "../dialogs/CompanyMemberEditDialog";
import {getDefaultMRTOptions} from "../base/BaseTable";
import {useAccessMarker} from "../../hooks/accessMarkerHooks";
import {ALL_ACCESS_FUNCTIONS} from "../../utils/accessControlUtils";
import {EditIcon} from "../icons/EditIcon";
import {authenticatedUserTableLocaleAtom} from "../../store/authenticatedUserStore";
import {useAtom} from "jotai";
import {User} from "../../types/domain/user";
import {useResourceManagement} from "../../hooks/resourceHooks";
import {getUserById} from "../../services/userService";

export interface Props {
    userId?: string;
    memberships: CompanyMemberItem[];
    isLoading: boolean;
    refreshCompanies: () => void;
}

export const UserCompaniesTable: React.FC<Props> = ({memberships, isLoading, refreshCompanies}) => {
    const access = useAccessMarker(ACCESS_FUNCTIONS);

    const [editDialogOpened, setEditDialogOpened] = useState<boolean>(false);
    const [activeMembershipItem, setActiveMembershipItem] = useState<CompanyMemberItem | undefined>(undefined);
    const [activeUser, setActiveUser] = useState<User>();
    const userManagement = useResourceManagement({getUserById});
    const [tableLocale] = useAtom(authenticatedUserTableLocaleAtom);
    const defaultTableOptions = getDefaultMRTOptions<CompanyMemberItem>(tableLocale);

    const onEditMemberDialogClose = (isSuccessful: boolean) => {
        setEditDialogOpened(false);
        if (isSuccessful) {
            refreshCompanies();
            setActiveUser(undefined);
        }
    };

    const table = useMaterialReactTable({
        ...defaultTableOptions, //spread your default options

        muiTableContainerProps: {
            sx: {
                maxHeight: "calc(100vh - 20rem)",
                minHeight: "calc(100vh - 20rem)",
            },
        },
        initialState: {
            ...defaultTableOptions.initialState,
            columnFilters: [
                {
                    id: "role",
                    value: "All",
                },
                {
                    id: "type",
                    value: "All",
                },
            ],
        },
        state: {
            isLoading,
        },
        columns,
        data: memberships,
        renderRowActions: ({row}) => (
            <Box sx={{display: "flex", gap: "1rem"}}>
                <IconButton
                    color="primary"
                    onClick={() => {
                        const member = row.original;
                        if (member?.membershipType == "Member") {
                            void userManagement.getUserById(member.member.userId).then((user) => {
                                setActiveUser(user);
                                setActiveMembershipItem(member);
                                setEditDialogOpened(true);
                            });
                        } else {
                            setActiveMembershipItem(member);
                            setEditDialogOpened(true);
                        }
                    }}
                >
                    <EditIcon
                        sx={{
                            stroke: "#303A8C",
                            width: "1.125rem",
                            height: "1.125rem",
                        }}
                    />
                </IconButton>
            </Box>
        ),
    });

    return (
        <>
            <MaterialReactTable table={table} />
            {editDialogOpened && activeMembershipItem && activeMembershipItem.membershipType === "Member" && (
                <CompanyMemberEditDialog
                    mode="edit"
                    companyId={activeMembershipItem.member.companyId}
                    member={activeMembershipItem.member}
                    user={activeUser}
                    managerRoleEnabled={access.canAssignManagerRole}
                    open={editDialogOpened}
                    onClose={onEditMemberDialogClose}
                />
            )}
            {editDialogOpened && activeMembershipItem && activeMembershipItem.membershipType === "Invited" && (
                <CompanyMemberInviteDialog
                    mode="edit"
                    companyId={activeMembershipItem.invitation.companyId}
                    invitation={activeMembershipItem.invitation}
                    open={editDialogOpened}
                    onClose={onEditMemberDialogClose}
                />
            )}
        </>
    );
};

const ROLES_OPTIONS = ["All", ...Object.values(CompanyMemberRole)];
const TYPE_OPTIONS = ["All", "Member", "Invited"];

const columns: MRT_ColumnDef<CompanyMemberItem>[] = [
    {
        id: "name",
        header: "Name",
        accessorFn: (row) => (row.membershipType === "Member" ? row.member.companyName : row.invitation.companyName),
        minSize: 100,
    },
    {
        id: "role",
        header: "Role",
        accessorKey: "role",
        minSize: 100,
        filterVariant: "select",
        filterSelectOptions: ROLES_OPTIONS,
        filterFn: (row, _, filter) => filter === "All" || row.original.role === filter,
    },
    {
        id: "type",
        header: "Membership",
        accessorKey: "membershipType",
        minSize: 100,
        filterVariant: "select",
        filterSelectOptions: TYPE_OPTIONS,
        filterFn: (row, _, filter) => filter === "All" || row.original.membershipType === filter,
    },
];

const ACCESS_FUNCTIONS = _.pick(ALL_ACCESS_FUNCTIONS, ["canAssignManagerRole"]);
